import { useRef, useState, FormEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Anchor, Loader2, ImagePlus, X } from "lucide-react";
import logoWhite from "@/assets/logo-white.svg";
import { uploadArtistFile, saveArtistUpload, type UploadedFile } from "@/lib/firebase";
import { usePageSEO } from "@/hooks/use-page-seo";

const NAVY = "#0B1622";

type Picked = {
  id: string;
  file: File;
  preview: string;
  isVideo: boolean;
  pct: number;
  status: "queued" | "uploading" | "done" | "error";
};

const uid = () =>
  (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// Accept images and videos (photos + phone clips). Some files report an empty
// MIME type, so fall back to the extension.
const MEDIA_EXT = /\.(jpe?g|png|gif|webp|heic|heif|mov|mp4|m4v|webm|avi|mkv|3gp|mpe?g)$/i;
const isMediaFile = (f: File) =>
  f.type.startsWith("image/") || f.type.startsWith("video/") || MEDIA_EXT.test(f.name);
const isVideoFile = (f: File) => f.type.startsWith("video/") || /\.(mov|mp4|m4v|webm|avi|mkv|3gp|mpe?g)$/i.test(f.name);

const fmtSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const ArtistUpload = () => {
  usePageSEO({
    title: "Artist Photo & Video Upload — Yacht Rock Detroit",
    description: "Upload your promo photos and videos for Yacht Rock Detroit.",
  });

  const [picked, setPicked] = useState<Picked[]>([]);
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [savedName, setSavedName] = useState("");
  const [savedCount, setSavedCount] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const next: Picked[] = [];
    for (const file of Array.from(list)) {
      if (!isMediaFile(file)) continue; // photos + videos only
      next.push({ id: uid(), file, preview: URL.createObjectURL(file), isVideo: isVideoFile(file), pct: 0, status: "queued" });
    }
    setPicked((prev) => [...prev, ...next].slice(0, 50));
    if (fileInput.current) fileInput.current.value = ""; // allow re-picking the same file
  };

  const removeFile = (id: string) => {
    setPicked((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone) URL.revokeObjectURL(gone.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const setPct = (id: string, pct: number, s?: Picked["status"]) =>
    setPicked((prev) => prev.map((p) => (p.id === id ? { ...p, pct, ...(s ? { status: s } : {}) } : p)));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const displayName = String(fd.get("displayName") || "").trim();
    const email = String(fd.get("email") || "").trim();

    if (!displayName) {
      setErrorMsg("Please enter how you'd like your name displayed.");
      setStatus("error");
      return;
    }
    if (!picked.length) {
      setErrorMsg("Please add at least one photo.");
      setStatus("error");
      return;
    }

    setStatus("saving");
    setErrorMsg("");
    const submissionId = uid();

    try {
      // Upload all files in parallel, each reporting its own progress.
      const uploaded: UploadedFile[] = await Promise.all(
        picked.map(async (p) => {
          setPct(p.id, 0, "uploading");
          const meta = await uploadArtistFile(submissionId, p.file, (pct) => setPct(p.id, pct));
          setPct(p.id, 100, "done");
          return meta;
        })
      );

      await saveArtistUpload({ displayName, email, files: uploaded });
      setSavedName(displayName);
      setSavedCount(uploaded.length);
      setStatus("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("artist upload failed", err);
      setStatus("error");
      setErrorMsg("Upload failed — check your connection and try again. Your photos weren't saved.");
    }
  };

  const label = "block text-[11px] font-bold uppercase tracking-[0.12em] text-white/55 mb-2";
  const input =
    "w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3.5 text-yacht-cream placeholder:text-white/30 text-[15px] outline-none transition focus:border-yacht-seaglass focus:ring-2 focus:ring-yacht-seaglass/25";

  const busy = status === "saving";

  return (
    <div
      className="min-h-screen text-yacht-cream flex flex-col items-center px-6 py-10 md:py-16"
      style={{ backgroundColor: NAVY }}
    >
      <a href="https://yachtrockdetroit.com" className="mb-8">
        <img src={logoWhite} alt="Yacht Rock Detroit" className="h-16 md:h-20 w-auto" />
      </a>

      <AnimatePresence mode="wait">
        {status === "done" ? (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-lg w-full text-center pt-10"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
            >
              <CheckCircle className="w-16 h-16 text-yacht-seaglass mx-auto mb-6" />
            </motion.div>
            <h1 className="font-['Anton'] uppercase text-3xl md:text-4xl leading-[1] mb-5">
              Files <span className="text-yacht-yellow">received</span>
            </h1>
            <p className="text-yacht-cream/80 text-lg leading-relaxed">
              Thanks, <b className="text-yacht-cream">{savedName}</b> — we got your{" "}
              {savedCount} {savedCount === 1 ? "file" : "files"}. Our team has them now and will use
              them for the festival. No need to send anything else.
            </p>
            <button
              type="button"
              onClick={() => {
                setPicked([]);
                setStatus("idle");
                setSavedName("");
              }}
              className="mt-8 inline-flex items-center gap-2 text-yacht-seaglass font-semibold"
            >
              <ImagePlus className="w-4 h-4" /> Upload more
            </button>
            <p className="mt-6 text-yacht-cream/70">See you on the river ⚓</p>
          </motion.div>
        ) : (
          <div key="form" className="max-w-lg w-full">
            <div className="text-center mb-9">
              <div className="inline-flex items-center gap-1.5 bg-yacht-seaglass text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-xs px-4 py-2 rounded-full border-2 border-white/85 -rotate-2 mb-5">
                <Anchor className="w-4 h-4" /> Artist Photo &amp; Video Upload
              </div>
              <h1 className="font-['Anton'] uppercase text-3xl md:text-4xl leading-[1]">
                Send us your <span className="text-yacht-yellow">photos &amp; videos</span>
              </h1>
              <p className="mt-4 text-yacht-cream/70 leading-relaxed">
                Drop your best promo photos and videos below so we can feature you. Add as many as you
                like — straight from your camera roll or files.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div>
                <label className={label}>How do you want your name displayed? *</label>
                <input
                  name="displayName"
                  required
                  placeholder="e.g. Yacht Rock Revue"
                  className={input}
                  autoComplete="off"
                />
              </div>

              <div>
                <label className={label}>Email (optional)</label>
                <input
                  name="email"
                  type="email"
                  placeholder="so we can reach you if needed"
                  className={input}
                />
              </div>

              <div>
                <label className={label}>Photos &amp; videos *</label>

                {/* Big tap target — opens camera roll / files / camera on phones */}
                <label
                  htmlFor="artist-files"
                  className="flex flex-col items-center justify-center gap-2 w-full rounded-xl border-2 border-dashed border-white/20 bg-white/[0.03] px-4 py-8 text-center cursor-pointer transition hover:border-yacht-seaglass/60 hover:bg-white/[0.05]"
                >
                  <ImagePlus className="w-8 h-8 text-yacht-seaglass" />
                  <span className="font-semibold text-yacht-cream">Tap to add photos or videos</span>
                  <span className="text-[13px] text-yacht-cream/55">
                    Camera roll, files, or record — MP4, MOV &amp; more · up to 50
                  </span>
                </label>
                <input
                  id="artist-files"
                  ref={fileInput}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />

                {/* Selected files with thumbnails + progress */}
                {picked.length > 0 && (
                  <div className="mt-4 space-y-2.5">
                    <div className="text-[12px] text-yacht-cream/55">
                      {picked.length} {picked.length === 1 ? "file" : "files"} selected
                    </div>
                    {picked.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-3 rounded-xl border border-white/12 bg-white/[0.03] p-2.5"
                      >
                        {p.isVideo ? (
                          <video
                            src={p.preview}
                            muted
                            playsInline
                            preload="metadata"
                            className="h-12 w-12 rounded-lg object-cover shrink-0 bg-black"
                          />
                        ) : (
                          <img
                            src={p.preview}
                            alt=""
                            className="h-12 w-12 rounded-lg object-cover shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] text-yacht-cream/90">{p.file.name}</div>
                          <div className="text-[11px] text-yacht-cream/45">{fmtSize(p.file.size)}</div>
                          {p.status === "uploading" && (
                            <div className="mt-1.5 h-1 w-full rounded-full bg-white/10">
                              <div
                                className="h-1 rounded-full bg-yacht-seaglass transition-all"
                                style={{ width: `${p.pct}%` }}
                              />
                            </div>
                          )}
                          {p.status === "done" && (
                            <div className="mt-1 text-[11px] text-yacht-seaglass">Uploaded ✓</div>
                          )}
                        </div>
                        {!busy && p.status !== "done" && (
                          <button
                            type="button"
                            onClick={() => removeFile(p.id)}
                            className="shrink-0 rounded-full p-1.5 text-white/50 hover:text-white hover:bg-white/10 transition"
                            aria-label="Remove photo"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {status === "error" && <p className="text-red-400 text-sm text-center">{errorMsg}</p>}

              <button
                type="submit"
                disabled={busy}
                className="w-full bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-lg py-4 rounded-xl shadow-[0_8px_0_#b5830f] hover:-translate-y-0.5 hover:shadow-[0_11px_0_#b5830f] active:translate-y-0 active:shadow-[0_4px_0_#b5830f] transition-all disabled:opacity-70 disabled:translate-y-0 flex items-center justify-center gap-2"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Uploading…
                  </>
                ) : (
                  "Send Photos"
                )}
              </button>
            </form>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArtistUpload;
